/**
 * Utility for lazy loading third-party scripts to improve initial page load performance
 */

// Cache for loaded scripts to avoid reloading
const loadedScripts = new Map<string, Promise<void>>();

/**
 * Lazy load a script by URL
 */
export function loadScript(src: string): Promise<void> {
    // Return cached promise if script is already loading/loaded
    if (loadedScripts.has(src)) {
        return loadedScripts.get(src)!;
    }

    const promise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.defer = true;
        
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        
        document.head.appendChild(script);
    });

    loadedScripts.set(src, promise);
    return promise;
}

/**
 * Lazy load Facebook Pixel
 */
let fbPixelInitialized = false;
let fbPixelPromise: Promise<any> | null = null;

export async function lazyLoadFacebookPixel(): Promise<any> {
    if (fbPixelInitialized && window.ReactPixel) {
        return window.ReactPixel;
    }

    if (fbPixelPromise) {
        return fbPixelPromise;
    }

    fbPixelPromise = import('react-facebook-pixel').then(module => {
        fbPixelInitialized = true;
        window.ReactPixel = module.default;
        return module.default;
    });

    return fbPixelPromise;
}

/**
 * Lazy load Microsoft Clarity
 */
export function lazyLoadClarity(clarityId: string): void {
    if (window.clarity) {
        return;
    }

    // Only load after user interaction or after a delay
    const loadClarity = () => {
        if (window.clarity) return;
        
        window.clarity = window.clarity || function() {
            (window.clarity.q = window.clarity.q || []).push(arguments);
        };
        
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.clarity.ms/tag/${clarityId}`;
        document.head.appendChild(script);
    };

    // Load on first user interaction
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    const loadOnInteraction = () => {
        loadClarity();
        events.forEach(event => {
            window.removeEventListener(event, loadOnInteraction);
        });
    };

    events.forEach(event => {
        window.addEventListener(event, loadOnInteraction, { once: true, passive: true });
    });

    // Also load after 5 seconds if no interaction
    setTimeout(loadClarity, 5000);
}

/**
 * Lazy load TikTok Pixel
 */
export function lazyLoadTikTokPixel(pixelId: string): void {
    if (window.ttq) {
        return;
    }

    // Initialize TikTok Pixel
    const loadTikTokPixel = () => {
        if (window.ttq) return;

        // TikTok Analytics Object initialization
        window.TiktokAnalyticsObject = 'ttq';
        const ttq = window.ttq = window.ttq || [];

        ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];

        ttq.setAndDefer = function(t: any, e: string) {
            t[e] = function() {
                t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            };
        };

        for (let i = 0; i < ttq.methods.length; i++) {
            ttq.setAndDefer(ttq, ttq.methods[i]);
        }

        ttq.instance = function(t: string) {
            const e = ttq._i[t] || [];
            for (let n = 0; n < ttq.methods.length; n++) {
                ttq.setAndDefer(e, ttq.methods[n]);
            }
            return e;
        };

        ttq.load = function(e: string, n?: any) {
            const r = "https://analytics.tiktok.com/i18n/pixel/events.js";
            ttq._i = ttq._i || {};
            ttq._i[e] = [];
            ttq._i[e]._u = r;
            ttq._t = ttq._t || {};
            ttq._t[e] = +new Date();
            ttq._o = ttq._o || {};
            ttq._o[e] = n || {};

            const script = document.createElement("script");
            script.type = "text/javascript";
            script.async = true;
            script.src = r + "?sdkid=" + e + "&lib=ttq";

            const firstScript = document.getElementsByTagName("script")[0];
            firstScript.parentNode!.insertBefore(script, firstScript);
        };

        // Load the pixel
        ttq.load(pixelId);
        ttq.page();
    };

    // Delay initialization to prioritize critical resources
    setTimeout(loadTikTokPixel, 100);
}

/**
 * Preload critical fonts
 */
export function preloadFonts(fonts: string[]): void {
    fonts.forEach(fontUrl => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'font';
        link.href = fontUrl;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
    });
}

// Extend Window interface
declare global {
    interface Window {
        ReactPixel?: any;
        clarity?: any;
        ttq?: any;
        TiktokAnalyticsObject?: string;
        // Intercom is already declared elsewhere
    }
}